import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PostCard from '../components/PostCard/PostCard';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import './Explore.css';

const Explore = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { exploreType } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    trendingNow: false,
    hashtags: false,
    risingCreators: false,
    freshVoices: false,
    activeCommunities: false
  });

  const mode = useMemo(() => (exploreType === 'long' ? 'long' : 'short'), [exploreType]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    // Keep /explore as an alias to the default short mode URL.
    if (!exploreType) {
      navigate('/explore/short', { replace: true });
    }
  }, [exploreType, navigate]);

  const loadExploreData = useCallback(async (targetMode = mode) => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = targetMode === 'long'
        ? '/api/feed/explore/long'
        : '/api/feed/explore/all';

      const response = await fetch(endpoint);
      if (!response.ok) throw new Error('Failed to fetch explore data');

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error loading explore data:', err);
      setError('Failed to load explore content. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    loadExploreData(mode);
  }, [mode, loadExploreData]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to hashtag feed for now (can be enhanced later)
      if (searchQuery.startsWith('#')) {
        navigate(`/tag/${searchQuery.slice(1)}`);
      } else {
        // Search for user
        navigate(`/user/${searchQuery.replace('@', '')}`);
      }
    }
  };

  const handleModeChange = (targetMode) => {
    if (targetMode !== mode) {
      navigate(`/explore/${targetMode}`);
    }
  };

  const handleHashtagClick = (tag, feedType = 'global') => {
    const safeTag = encodeURIComponent((tag || '').replace(/^#+/, '').trim().toLowerCase());
    const safeFeedType = (feedType || 'global').toLowerCase();
    if (!safeTag) return;
    navigate(`/feed/${safeFeedType}/tag/${safeTag}`);
  };

  const handleUserClick = (username) => {
    navigate(`/user/${username}`);
  };

  const handleCardClick = (e, callback) => {
    e.stopPropagation(); // Prevent section toggle when clicking cards
    callback();
  };

  const modeTabs = (
    <div className="explore-mode-tabs" role="tablist" aria-label="Explore mode">
      <button
        type="button"
        className={`explore-mode-tab ${mode === 'short' ? 'active' : ''}`}
        onClick={() => handleModeChange('short')}
      >
        Explore Short
      </button>
      <button
        type="button"
        className={`explore-mode-tab ${mode === 'long' ? 'active' : ''}`}
        onClick={() => handleModeChange('long')}
      >
        Explore Long
      </button>
    </div>
  );

  if (loading) {
    return (
      <>
        <SidebarHeader title="Explore" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        {modeTabs}
        <div className="explore-container">
          <LoadingSpinner text={`Loading ${mode} explore page...`} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SidebarHeader title="Explore" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        {modeTabs}
        <div className="explore-container">
          <div className="error-box">
            <p>{error}</p>
            <button onClick={() => loadExploreData(mode)} className="retry-btn">
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SidebarHeader title="Explore" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      {modeTabs}
      <div className="explore-container">
        {/* Header with Search */}
        <div className="explore-header">
          <h1 className="explore-title">{mode === 'short' ? 'Explore Short' : 'Explore Long'}</h1>
          <form className="explore-search" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search users, tags, or communities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="explore-search-input"
            />
            <button type="submit" className="explore-search-btn">
              Search
            </button>
          </form>
        </div>

        {mode === 'short' && (
          <>
            {/* Trending Now Section */}
            {data?.trendingNow && data.trendingNow.length > 0 && (
              <section className="explore-section">
                <div className="section-header" onClick={() => toggleSection('trendingNow')}>
                  <div className="section-title-row">
                    <h2 className="section-title">Trending Now</h2>
                    <button className={`expand-btn ${expandedSections.trendingNow ? 'expanded' : ''}`}>
                      {expandedSections.trendingNow ? '-' : '+'}
                    </button>
                  </div>
                  <span className="section-subtitle">Trending short posts in the last 24 hours</span>
                </div>
                {expandedSections.trendingNow && (
                  <>
                    <div className="posts-grid">
                      {data.trendingNow.map((post, index) => (
                        <PostCard key={`${post.author}-${post.permlink}-${index}`} post={post} showActions={true} />
                      ))}
                    </div>
                    <button className="collapse-btn-bottom" onClick={() => toggleSection('trendingNow')}>
                      Collapse Section ^
                    </button>
                  </>
                )}
              </section>
            )}

            {/* Popular Hashtags Section */}
            {data?.hashtags && data.hashtags.length > 0 && (
              <section className="explore-section">
                <div className="section-header" onClick={() => toggleSection('hashtags')}>
                  <div className="section-title-row">
                    <h2 className="section-title">Popular Hashtags</h2>
                    <button className={`expand-btn ${expandedSections.hashtags ? 'expanded' : ''}`}>
                      {expandedSections.hashtags ? '-' : '+'}
                    </button>
                  </div>
                  <span className="section-subtitle">Trending topics this week</span>
                </div>
                {expandedSections.hashtags && (
                  <>
                    <div className="hashtags-grid">
                      {data.hashtags.map((hashtag, index) => (
                        <button
                          key={`${hashtag.tag}-${index}`}
                          className="hashtag-card"
                          onClick={(e) => handleCardClick(e, () => handleHashtagClick(hashtag.tag, hashtag.feedType || 'global'))}
                        >
                          <span className="hashtag-name">#{hashtag.tag}</span>
                          <span className="hashtag-count">{hashtag.count.toLocaleString()} posts</span>
                        </button>
                      ))}
                    </div>
                    <button className="collapse-btn-bottom" onClick={() => toggleSection('hashtags')}>
                      Collapse Section ^
                    </button>
                  </>
                )}
              </section>
            )}

            {/* Rising Creators Section */}
            {data?.risingCreators && data.risingCreators.length > 0 && (
              <section className="explore-section">
                <div className="section-header" onClick={() => toggleSection('risingCreators')}>
                  <div className="section-title-row">
                    <h2 className="section-title">Rising Creators</h2>
                    <button className={`expand-btn ${expandedSections.risingCreators ? 'expanded' : ''}`}>
                      {expandedSections.risingCreators ? '-' : '+'}
                    </button>
                  </div>
                  <span className="section-subtitle">Active authors gaining traction</span>
                </div>
                {expandedSections.risingCreators && (
                  <>
                    <div className="creators-grid">
                      {data.risingCreators.map((creator, index) => (
                        <button
                          key={`${creator.username}-${index}`}
                          className="creator-card"
                          onClick={(e) => handleCardClick(e, () => handleUserClick(creator.username))}
                        >
                          <div className="creator-name">@{creator.username}</div>
                          <div className="creator-stats">
                            <span>{creator.postCount} posts</span>
                            <span>•</span>
                            <span>{creator.totalVotes} votes</span>
                            <span>•</span>
                            <span>{creator.avgVotes} avg</span>
                          </div>
                        </button>
                      ))}
                    </div>
                    <button className="collapse-btn-bottom" onClick={() => toggleSection('risingCreators')}>
                      Collapse Section ^
                    </button>
                  </>
                )}
              </section>
            )}

            {/* Fresh Voices Section */}
            {data?.freshVoices && data.freshVoices.length > 0 && (
              <section className="explore-section">
                <div className="section-header" onClick={() => toggleSection('freshVoices')}>
                  <div className="section-title-row">
                    <h2 className="section-title">Fresh Voices</h2>
                    <button className={`expand-btn ${expandedSections.freshVoices ? 'expanded' : ''}`}>
                      {expandedSections.freshVoices ? '-' : '+'}
                    </button>
                  </div>
                  <span className="section-subtitle">Discover new creators</span>
                </div>
                {expandedSections.freshVoices && (
                  <>
                    <div className="posts-grid">
                      {data.freshVoices.map((post, index) => (
                        <PostCard key={`${post.author}-${post.permlink}-${index}`} post={post} showActions={true} />
                      ))}
                    </div>
                    <button className="collapse-btn-bottom" onClick={() => toggleSection('freshVoices')}>
                      Collapse Section ^
                    </button>
                  </>
                )}
              </section>
            )}

            {/* Active Communities Section */}
            {data?.activeCommunities && data.activeCommunities.length > 0 && (
              <section className="explore-section">
                <div className="section-header" onClick={() => toggleSection('activeCommunities')}>
                  <div className="section-title-row">
                    <h2 className="section-title">Active Communities</h2>
                    <button className={`expand-btn ${expandedSections.activeCommunities ? 'expanded' : ''}`}>
                      {expandedSections.activeCommunities ? '-' : '+'}
                    </button>
                  </div>
                  <span className="section-subtitle">Communities with recent activity</span>
                </div>
                {expandedSections.activeCommunities && (
                  <>
                    <div className="communities-grid">
                      {data.activeCommunities.map((community, index) => (
                        <button
                          key={`${community.parentAuthor}-${community.parentPermlink}-${index}`}
                          className="community-card"
                          onClick={(e) => handleCardClick(e, () => navigate(`/@${community.parentAuthor}/${community.parentPermlink}`))}
                        >
                          <div className="community-name">
                            @{community.parentAuthor}/{community.parentPermlink}
                          </div>
                          <div className="community-stats">
                            <span>{community.postCount} posts this week</span>
                          </div>
                        </button>
                      ))}
                    </div>
                    <button className="collapse-btn-bottom" onClick={() => toggleSection('activeCommunities')}>
                      Collapse Section ^
                    </button>
                  </>
                )}
              </section>
            )}
          </>
        )}

        {mode === 'long' && (
          <>
            {data?.trendingArticles && data.trendingArticles.length > 0 && (
              <section className="explore-section static-section">
                <div className="section-title-row">
                  <h2 className="section-title">Trending Articles</h2>
                </div>
                <span className="section-subtitle">Most engaged long-form posts</span>
                <div className="posts-grid">
                  {data.trendingArticles.map((post, index) => (
                    <PostCard key={`${post.author}-${post.permlink}-${index}`} post={post} showActions={true} />
                  ))}
                </div>
              </section>
            )}

            {data?.emergingArticles && data.emergingArticles.length > 0 && (
              <section className="explore-section static-section">
                <div className="section-title-row">
                  <h2 className="section-title">Emerging Articles</h2>
                </div>
                <span className="section-subtitle">Fresh long-form posts from the last 14 days</span>
                <div className="posts-grid">
                  {data.emergingArticles.map((post, index) => (
                    <PostCard key={`${post.author}-${post.permlink}-${index}`} post={post} showActions={true} />
                  ))}
                </div>
              </section>
            )}

            {(!data?.trendingArticles?.length && !data?.emergingArticles?.length) && (
              <section className="explore-section static-section">
                <div className="section-title-row">
                  <h2 className="section-title">No long-form posts yet</h2>
                </div>
                <span className="section-subtitle">Try refreshing after your long-form cache sync runs.</span>
              </section>
            )}
          </>
        )}

        {/* Refresh Button */}
        <div className="explore-footer">
          <button onClick={() => loadExploreData(mode)} className="refresh-btn">
            Refresh Content
          </button>
        </div>
      </div>
    </>
  );
};

export default Explore;
